// ─── Encrypt-on-send / decrypt-on-render — the bridge between crypto/chat.js
// and firebase/firestore.js for actual message content ───────────────────────
// See CHAT_E2E_DESIGN.md §4 (the unified DM/group encryption scheme) and §6
// (message data model). crypto/chat.js is deliberately kept network-free and
// firebase/firestore.js is deliberately kept crypto-free (it just persists
// whatever ciphertext it's handed) — this file is where those two meet, so
// the "who do I need to fetch public keys for, and how do I assemble the
// wrappedKeys map" orchestration lives in exactly one place rather than
// being duplicated in every component that needs to send or read a message.
//
// Nothing in this file ever sees a private key it didn't receive directly
// from the caller (i.e. from ChatIdentityContext's already-unlocked
// in-memory identity) — it never reads or writes IndexedDB or
// chatKeyBackups itself.

import {
  generateSymmetricKey,
  secretboxEncrypt,
  secretboxDecrypt,
  boxWrapKey,
  boxUnwrapKey,
} from '../../crypto/chat';
import { getChatKey, addChatMessage } from '../../firebase/firestore';

// Public keys never change once generated — there's no key-rotation flow in
// v1 (design doc §9) — so a plain in-memory Map is a safe, simple cache
// across the whole tab's lifetime. Keyed by userId; never invalidated.
// Exported so encryptedAttachments.js (Task #150) shares this one cache
// rather than each file maintaining its own — same public keys, same
// lifetime assumption, no reason to fetch twice.
const publicKeyCache = new Map();

export async function getMemberPublicKey(companyId, userId) {
  if (publicKeyCache.has(userId)) return publicKeyCache.get(userId);
  const record = await getChatKey(companyId, userId);
  const publicKey = record?.publicKey || null;
  if (publicKey) publicKeyCache.set(userId, publicKey);
  return publicKey;
}

// Encrypts `text` with a fresh one-time key, wraps that key to every current
// thread member (including the sender, so they can re-read their own sent
// messages after reload — design doc §4 point 3), and persists the
// ciphertext-only document via firestore.js. A member who has never set up
// chat on any device (no chatKeys/{userId} doc yet) has no public key to
// encrypt to and is silently skipped for THIS message only — they simply
// won't be able to read it; there's nothing else to do, and it doesn't block
// everyone else in the thread from receiving it.
export async function encryptAndSendMessage({ companyId, thread, userId, identity, text }) {
  if (!companyId || !thread?.id || !userId || !identity?.privateKey || !identity?.publicKey) {
    throw new Error('Cannot send — chat is not ready');
  }
  const trimmed = (text || '').trim();
  if (!trimmed) return null;

  const memberIds = thread.memberIds || [];
  const mk = await generateSymmetricKey();
  const { ciphertext, nonce } = await secretboxEncrypt(trimmed, mk);

  const wrappedKeys = {};
  await Promise.all(memberIds.map(async (memberId) => {
    const recipientPublicKey = await getMemberPublicKey(companyId, memberId);
    if (!recipientPublicKey) return; // hasn't set up chat keys yet — nothing to wrap to
    const { wrappedKey, nonce: nonce2 } = await boxWrapKey(mk, recipientPublicKey, identity.privateKey);
    wrappedKeys[memberId] = { wrappedKey, nonce2 };
  }));

  if (Object.keys(wrappedKeys).length === 0) {
    throw new Error('No recipients have set up chat encryption yet — message not sent');
  }

  return addChatMessage(companyId, thread.id, {
    senderId: userId,
    senderPublicKey: identity.publicKey,
    type: 'text',
    ciphertext,
    nonce,
    wrappedKeys,
  });
}

// Reverses the above for one message, from the viewing user's perspective.
// Throws (never returns a fallback string) on any failure — no key for this
// user on this message (e.g. they joined the group after it was sent, which
// is expected/by-design per §4, not an error condition exactly, but still
// "can't decrypt"), wrong keypair, or tampered ciphertext. Callers should
// catch this and render the design doc's standard "🔒 can't decrypt this"
// placeholder rather than let it propagate.
export async function decryptMessage(msg, identity, userId) {
  if (!identity?.privateKey) throw new Error('Chat is locked');
  const myEntry = msg.wrappedKeys?.[userId];
  if (!myEntry) throw new Error('No key for you on this message');
  const mk = await boxUnwrapKey(myEntry.wrappedKey, myEntry.nonce2, msg.senderPublicKey, identity.privateKey);
  return secretboxDecrypt(msg.ciphertext, msg.nonce, mk);
}
